// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Alert, Button, IconButton, AppIcon, Spinner, Modal } from '@loa/ui-components';
import { clientListApi } from '../api/clientListApi';
import { clientApi } from '../api/clientApi';
import { ClientOverviewTab } from '../components/client/ClientOverviewTab';
import { ClientPoliciesTab } from '../components/client/ClientPoliciesTab';
import { ClientLOAsTab } from '../components/client/ClientLOAsTab';
import { ClientLinkedRelationshipsTab } from '../components/client/ClientLinkedRelationshipsTab';
import { ClientLinkedBankAccountsTab } from '../components/client/ClientLinkedBankAccountsTab';
import { ClientMeetingsTab } from '../components/client/ClientMeetingsTab';
import { CopyTagButton } from '../components/integrations/CopyTagButton';
import { ClientActivityTab } from '../components/client/ClientActivityTab';
import { ClientForm } from '../components/ClientForm';
import type { ClientDetailDTO } from '../types/clientList';
import type { Client, ClientCreateDTO, ClientUpdateDTO, ClientStatus } from '../types/client';
import { ClientStatusBadge } from '../components/ClientStatusBadge';
import { TabBar } from '../components/TabBar';
import { SectionTitle } from '../components/SectionTitle';
import { SectionSubtitle } from '../components/SectionSubtitle';
import { usePageHeader } from '../contexts/PageHeaderContext';
import { usePageContextRegister } from '../contexts/PageContextProvider';
import { EntityTagEditor } from '../components/tags/EntityTagEditor';
import { useEntityTags } from '../hooks/useEntityTags';
import './ClientDetailPage.css';

export function ClientDetailPage() {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const { setBreadcrumbItems, setBackTo } = usePageHeader();
  const [activeTab, setActiveTab] = useState<'overview' | 'policies' | 'loas' | 'linked' | 'bankAccounts' | 'meetings' | 'activity'>('overview');
  const [client, setClient] = useState<ClientDetailDTO | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  usePageContextRegister({
    pageType: 'CLIENT_DETAIL',
    entityIds: clientId
      ? { clientId, profileId: client?.profileId }
      : undefined,
    pageTitle: client ? `${client.firstName ?? ''} ${client.lastName ?? ''}`.trim() : undefined,
  });

  // Edit Client Modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [clientForEdit, setClientForEdit] = useState<Client | null>(null);

  // Alert states
  const [showExecutiveSummaryAlert, setShowExecutiveSummaryAlert] = useState(false);

  // Tag chips + picker on the client header (Tags PRD §9).
  const { tagsByEntityId } = useEntityTags('CLIENT', clientId ? [clientId] : []);
  const clientTags = clientId ? tagsByEntityId[clientId] ?? [] : [];

  useEffect(() => {
    const loadClient = async () => {
      if (!clientId) return;

      try {
        setIsLoading(true);
        setError(null);
        const data = await clientListApi.getClientDetail(clientId);
        setClient(data);
      } catch (err) {
        console.error('Failed to load client:', err);
        setError(err as Error);
      } finally {
        setIsLoading(false);
      }
    };

    loadClient();
  }, [clientId]);

  useEffect(() => {
    if (client?.profileId && client?.profileName) {
      setBreadcrumbItems([
        { label: 'Relationships', to: '/relationships' },
        { label: client.profileName, to: `/relationships/${client.profileId}` },
        { label: 'Clients' },
        { label: `${client.firstName} ${client.lastName}` },
      ]);
      setBackTo(`/relationships/${client.profileId}`);
    }
    return () => {
      setBreadcrumbItems(null);
      setBackTo(null);
    };
  }, [client?.profileId, client?.profileName, client?.firstName, client?.lastName, setBreadcrumbItems, setBackTo]);

  const handleEditClient = async () => {
    if (!client) return;
    try {
      // Load full client data for editing
      const fullClient = await clientApi.getClient(client.profileId, client.id);
      setClientForEdit(fullClient);
      setIsEditModalOpen(true);
    } catch (err) {
      console.error('Failed to load client for editing:', err);
    }
  };

  const handleUpdateClient = async (data: ClientCreateDTO | ClientUpdateDTO) => {
    if (!client) return;
    try {
      setIsUpdating(true);
      await clientApi.updateClient(client.profileId, client.id, data as ClientUpdateDTO);
      // Reload client details to reflect changes
      const updatedClient = await clientListApi.getClientDetail(client.id);
      setClient(updatedClient);
      setIsEditModalOpen(false);
      setClientForEdit(null);
    } catch (err) {
      console.error('Failed to update client:', err);
      throw err;
    } finally {
      setIsUpdating(false);
    }
  };

  const handleNudgeClient = () => {
    if (client) {
      navigate(`/resend-invitation?profileId=${client.profileId}&clientId=${client.id}`);
    }
  };

  if (isLoading) {
    return (
      <div style={{ marginTop: '2em' }}>
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ marginTop: '2em' }}>
        <Alert variant="error" header="Error Loading Client">
          <p>{error.message || 'Failed to load client details'}</p>
        </Alert>
      </div>
    );
  }

  if (!client) {
    return (
      <div style={{ marginTop: '2em' }}>
        <Alert variant="warning" header="Client Not Found">
          <p>The requested client could not be found.</p>
        </Alert>
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      {/* Client Header Card */}
      <div className="flex bg-white gap-lg flex-col">
        <div className="flex flex">
          {/* Client Info */}
          <div className="flex flex-col w-full items-center px-lg pt-lg">
            <div className="flex flex-row justify-between w-full">
              <div className='flex flex-col'>
                <div className='flex items-center gap-sm'>
                  <SectionTitle>{client.firstName} {client.lastName}</SectionTitle>
                  <CopyTagButton entityKind="Client" entityId={client.id} />
                </div>
                <SectionSubtitle>CRM #{client.crmPersonNumber}</SectionSubtitle>
                <div className="mt-sm">
                  <EntityTagEditor entityType="CLIENT" entityId={client.id} tags={clientTags} />
                </div>
              </div>
              <div className='flex flex-row gap-lg items-center'>
                <ClientStatusBadge status={client.status as ClientStatus} />
                <div className='flex items-center gap-sm'>
                  <Button variant="filled" size="md" onClick={handleNudgeClient}>Nudge Client</Button>
                  <IconButton variant="square-outlined" size="md" onClick={handleEditClient} icon={<AppIcon name="edit" />} />
                </div>
              </div>
            </div>
            <TabBar
              tabs={[
                { key: 'overview', label: 'Overview' },
                { key: 'policies', label: 'Policies', count: client.totalPolicies || 0 },
                { key: 'loas', label: 'LOAs', count: client.totalLoas || 0 },
                { key: 'linked', label: 'Linked Relationships' },
                { key: 'bankAccounts', label: 'Linked Bank Accounts' },
                { key: 'meetings', label: 'Meetings' },
                { key: 'activity', label: 'Activity' },
              ]}
              activeTab={activeTab}
              onTabChange={(key) => setActiveTab(key as typeof activeTab)}
              className="mt-lg"
            />
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="bg-page-bg p-lg min-h-[calc(100vh-80px)] overflow-auto">
        {activeTab === 'overview' && <ClientOverviewTab client={client} />}
        {activeTab === 'policies' && <ClientPoliciesTab clientId={client.id} profileId={client.profileId} />}
        {activeTab === 'loas' && <ClientLOAsTab clientId={client.id} />}
        {activeTab === 'linked' && <ClientLinkedRelationshipsTab client={client} />}
        {activeTab === 'bankAccounts' && (
          <ClientLinkedBankAccountsTab
            federatedUserId={client.federatedUserId}
            clientName={`${client.firstName} ${client.lastName}`}
          />
        )}
        {activeTab === 'meetings' && <ClientMeetingsTab clientId={client.id} />}
        {activeTab === 'activity' && <ClientActivityTab profileId={client.profileId} />}
      </div>

      {/* Edit Client Modal */}
      <Modal
        open={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setClientForEdit(null);
        }}
        size="small"
        className="client-edit-modal"
      >
        <Modal.Content className="!p-0">
          {clientForEdit && (
            <ClientForm
              client={clientForEdit}
              onSubmit={handleUpdateClient}
              onCancel={() => {
                setIsEditModalOpen(false);
                setClientForEdit(null);
              }}
              loading={isUpdating}
            />
          )}
        </Modal.Content>
      </Modal>

      {/* Executive Summary Alert */}
      <Modal
        open={showExecutiveSummaryAlert}
        onClose={() => setShowExecutiveSummaryAlert(false)}
        size="small"
      >
        <Modal.Header title="Executive Summary" onClose={() => setShowExecutiveSummaryAlert(false)} />
        <Modal.Content>
          <Alert variant="info" header="Coming Soon">
            <p>The Executive Summary feature is currently under development and will be available soon.</p>
          </Alert>
        </Modal.Content>
        <Modal.Actions
          primaryAction={{ label: 'Close', onClick: () => setShowExecutiveSummaryAlert(false) }}
        />
      </Modal>
    </div>
  );
}
